import { schema } from '@edgevanta/db/drizzle/client';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';
import { createQuerySchemaWithPagination } from '../shared';

export const selectItemSchema = createSelectSchema(schema.items);

export const insertItemSchema = createInsertSchema(schema.items, {
  number: z.string().min(1),
  unit: z.string().min(1),
  ownerId: z.string().uuid(),
});

export const updateItemSchema = insertItemSchema.partial().extend({
  id: z.string(),
});

const arrayPreprocess = (val: unknown) => {
  if (typeof val === 'string' && val.length > 0) {
    const items = val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return items.length > 0 ? items : undefined;
  }
  if (Array.isArray(val)) {
    const items = val.map((s) => String(s).trim()).filter(Boolean);
    return items.length > 0 ? items : undefined;
  }
  return undefined;
};

const ItemsQuerySchema = z.object({
  number: z.string().optional(),
  ownerId: z.string().optional(),
  stateId: z.string().uuid().optional(),
  categoryIds: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  collectionIds: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  unit: z.string().optional(),
  isTrueLumpSum: z
    .string()
    .optional()
    .transform((v) => (v === 'true' ? true : v === 'false' ? false : undefined))
    .optional(),
});

export const getItemsQuerySchema = createQuerySchemaWithPagination(
  ItemsQuerySchema,
  { id: 'number', desc: false },
);

export const getItemsCountQuerySchema = ItemsQuerySchema;

const itemHistorySortSchema = z
  .string()
  .optional()
  .transform((val) => {
    const defaultSort = [{ id: 'lettingDate', desc: true }];
    if (!val) {
      return defaultSort;
    }

    try {
      const parsed = JSON.parse(val);
      const sortSchema = z.array(
        z.object({
          id: z.enum([
            'lettingDate',
            'vendorName',
            'county',
            'quantity',
            'unitPrice',
            'extension',
            'rank',
            'callOrder',
            'contractId',
            'contractType',
            'projectNumber',
            'workType',
            'proposalDescription',
            'route',
            'costEstimate',
            'completionDate',
            'completionCalendarDays',
            'completionWorkingDays',
            'lineNumber',
            'bidderCount',
          ]),
          desc: z.boolean(),
        }),
      );

      const result = sortSchema.safeParse(parsed);

      if (!result.success) {
        return defaultSort;
      }

      return result.data;
    } catch {
      return defaultSort;
    }
  })
  .default([]);

export const getItemHistoryQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(5000).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  search: z.string().optional(),
  ownerId: z.string().uuid().optional(),
  vendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  globalVendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  counties: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  districts: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  years: z.preprocess(arrayPreprocess, z.array(z.coerce.number()).optional()),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minQuantity: z.coerce.number().optional(),
  maxQuantity: z.coerce.number().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  ranks: z.preprocess(
    arrayPreprocess,
    z.array(z.coerce.number().int().min(1).max(5)).optional(),
  ),
  sort: itemHistorySortSchema,
});

export const getItemStatsQuerySchema = z.object({
  search: z.string().optional(),
  ownerId: z.string().uuid().optional(),
  vendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  globalVendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  counties: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  districts: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  years: z.preprocess(arrayPreprocess, z.array(z.coerce.number()).optional()),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minQuantity: z.coerce.number().optional(),
  maxQuantity: z.coerce.number().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  ranks: z.preprocess(
    arrayPreprocess,
    z.array(z.coerce.number().int().min(1).max(5)).optional(),
  ),
});

export const getItemGeographyQuerySchema = z.object({
  search: z.string().optional(),
  ownerId: z.string().uuid().optional(),
  vendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  globalVendorIds: z.preprocess(
    arrayPreprocess,
    z.array(z.string().uuid()).optional(),
  ),
  counties: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  districts: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  years: z.preprocess(arrayPreprocess, z.array(z.coerce.number()).optional()),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minQuantity: z.coerce.number().optional(),
  maxQuantity: z.coerce.number().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  ranks: z.preprocess(
    arrayPreprocess,
    z.array(z.coerce.number().int().min(1).max(5)).optional(),
  ),
});

export const getItemVendorsQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(50).optional().default(15),
  cursor: z.string().optional(),
  sortBy: z
    .enum(['winRate', 'bidCount', 'avgPrice', 'vsMarket', 'lastBidDate'])
    .optional()
    .default('winRate'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  counties: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  districts: z.preprocess(arrayPreprocess, z.array(z.string()).optional()),
  minBids: z.coerce.number().min(1).optional(),
  minQuantity: z.coerce.number().optional(),
  maxQuantity: z.coerce.number().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
});
